import React, { MouseEvent } from 'react'
import styled, { css, SimpleInterpolation } from 'styled-components'

import Link from '../atoms/link'
import theme from '../atoms/theme'

const { colours, animation } = theme

export interface ButtonProps {
  children: string | JSX.Element
  to?: string
  onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  disabled?: boolean
  type?: 'primary' | 'secondary' | 'alternative'
  active?: boolean
  small?: boolean
  style?: object
  className?: string
  tabIndex?: number
}

// Separate interface for HTML button props to avoid conflicts
interface HTMLButtonProps extends Omit<ButtonProps, 'type'> {
  type?: 'button' | 'submit' | 'reset'
}

export const buttonColor = (color: string): SimpleInterpolation => {
  return css`
    background-color: ${color};
    border: 0.1rem solid ${color};
    box-shadow: 0 0 0.1rem 0 ${color} inset, 0 0 0.1rem 0 ${color};
  `
}

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`

// Suppressed as small needs to be pulled out of props
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const BaseButton = ({ small, ...props }: ButtonProps): JSX.Element => {
  // Extract props not needed for HTML button element
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { active, type, ...restOfProps } = props

  return props.to ? (
    <StyledLink to={props.to} {...restOfProps}>
      {props.children}
    </StyledLink>
  ) : (
    <button {...(restOfProps as HTMLButtonProps)} type='button'>
      {props.children}
    </button>
  )
}

export const PrimaryButton = styled(BaseButton)`
  ${(props): SimpleInterpolation => (props.active ? buttonColor(colours.primaryTint1) : buttonColor(colours.primary))};
  border-radius: 5rem;
  cursor: ${(props): string => (props.disabled ? 'default' : 'pointer')};
  color: ${colours.light};
  font-size: 1.4rem;
  line-height: 1.4rem;
  padding-right: 2.2rem;
  padding-left: 2.2rem;
  margin-right: 1rem;
  text-decoration: none;
  transition: background-color ${animation.defaultLength} ease-in-out, border ${animation.defaultLength} ease-in-out,
    box-shadow ${animation.defaultLength} ease-in-out;
  height: ${(props): string => (props.small ? '2.5rem' : '5rem')};

  @media only screen and ${theme.breakpoints.fromNormalScreen} {
    height: ${(props): string => (props.small ? '3.2rem' : '5rem')};
  }

  &:hover {
    ${buttonColor(colours.primaryTint1)};
  }

  &:active {
    ${buttonColor(colours.primaryTint1)};
    color: ${colours.light};
  }

  &:disabled {
    ${buttonColor(colours.lightTint3)};
  }
`

const AlternativeButton = styled(PrimaryButton)`
  ${(props): SimpleInterpolation => (props.active ? buttonColor(colours.lightTint1) : buttonColor(colours.dark))};
  color: ${colours.light};

  &:hover {
    ${buttonColor(colours.lightTint1)};
  }

  &:active {
    ${buttonColor(colours.lightTint1)};
  }

  &:disabled {
    ${buttonColor(colours.lightTint3)};
    color: ${colours.light};
  }
`

export const SecondaryButton = styled(PrimaryButton)`
  background-color: ${(props): string => (props.active ? colours.primary : 'transparent')};
  border: 0.1rem solid ${colours.primary};
  box-shadow: 0 0 0.1rem 0 ${colours.primary} inset, 0 0 0.1rem 0 ${colours.primary};
  height: 3rem;
  &:hover {
    ${buttonColor(colours.primary)};
  }
  &:active {
    ${buttonColor(colours.primary)};
  }
  &:disabled {
    ${buttonColor(colours.lightTint1)};
    color: ${colours.light};
  }
`

const Button = ({ small, ...props }: ButtonProps): JSX.Element => {
  switch (props.type) {
    case 'alternative':
      return (
        <AlternativeButton small={small} {...props}>
          {props.children}
        </AlternativeButton>
      )

    case 'secondary':
      return (
        <SecondaryButton small={small} {...props}>
          {props.children}
        </SecondaryButton>
      )

    default:
      return (
        <PrimaryButton small={small} {...props}>
          {props.children}
        </PrimaryButton>
      )
  }
}

export default Button
